import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { fromEvent, Observable, Subject, takeUntil } from 'rxjs';
import { UiControlDirective } from '../../../directives/ui/ui-control.directive';
import {
  IModalSettings,
  ModalSettings,
  UI_POPUP_HIDE_STRATEGY,
  UTILS,
} from '@aksia/infrastructure';
import { UiContentComponentDirective } from '../../../directives/ui/ui-content-component.directive';
import { UiLoadingDirective } from '../../../directives/ui/ui-loading.directive';
import { RouterOutlet } from '@angular/router';
import { JsonPipe, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { ButtonComponent } from '../../input/button/button.component';

@Component({
  selector: 'modal',
  imports: [
    UiLoadingDirective,
    RouterOutlet,
    NgTemplateOutlet,
    NgComponentOutlet,
    ButtonComponent,
    JsonPipe,
  ],
  templateUrl: './modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    {
      directive: UiContentComponentDirective,
      inputs: [
        'uicontentcomponent',
        'uicontentinput',
        'uicontenttemplate',
        'uicontentcontext',
        'uicontentcontextmore',
      ],
    },
    {
      directive: UiLoadingDirective,
      inputs: ['uiloading:modalloading'],
    },
  ],
})
export class ModalComponent extends UiControlDirective {
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  //#region Injections

  protected readonly contentDir = inject(UiContentComponentDirective);
  protected readonly loadingDir = inject(UiLoadingDirective);

  //#endregion

  //#region Constants

  protected readonly JSON = JSON;

  //#endregion

  //#region Inputs/Outputs

  override settings = input(new ModalSettings(), {
    transform: (settings: IModalSettings) => this.initSettings(settings),
  });

  text = model<string | undefined>(undefined);
  onModalOpen = output();
  onModalClose = output();

  //#endregion

  //#region Host Bindings

  @HostBinding('style.display') get display() {
    return this.isOpen() ? 'block' : 'none';
  }

  //#endregion

  //#region View Children

  modal = viewChild('modal', { read: ElementRef });

  //#endregion

  //#region Properties

  protected title: WritableSignal<string | undefined> = signal(undefined);
  modalListener$?: Observable<any>;
  modalClosed$: Subject<boolean> = new Subject<boolean>();
  data?: unknown;

  //#endregion
  //#region Functions

  constructor() {
    super();
    afterNextRender(() => {
      this.onModalOpen.emit();
    });
  }

  protected override initSettings(settings: IModalSettings) {
    let modalSettings = UTILS.OBJECT.assign(new ModalSettings(), settings);
    super.initSettings(modalSettings);
    return modalSettings;
  }

  private enableCloseOnBackdrop(enable: boolean) {
    const listener = (event: MouseEvent) => {
      if ((event.target as HTMLElement)?.nodeName === 'DIALOG') {
        this.close();
      }
    };

    if (enable) {
      this.modal()!.nativeElement.onclick = listener;
    } else {
      this.modal()?.nativeElement?.removeEventListener('click', listener);
    }
  }

  private dialogContainsTarget(event: MouseEvent) {
    const dialog = this.modal()?.nativeElement;

    if (!dialog) {
      return;
    }

    // Get dialog's bounding rectangle
    const rect = dialog.getBoundingClientRect();
    // Check if click is outside the dialog
    return (
      rect.top <= event.clientY &&
      event.clientY <= rect.bottom &&
      rect.left <= event.clientX &&
      event.clientX <= rect.right
    );
  }

  public isOpen(): boolean {
    let dialog = this.modal()?.nativeElement as HTMLDialogElement;
    return dialog?.hasAttribute('open');
  }

  public open(
    overrideTitle?: string,
    overrideText?: string,
    hasBackdrop?: boolean,
    context?: unknown,
  ) {
    let dialog = this.modal()?.nativeElement as HTMLDialogElement;

    if (overrideTitle) {
      this.label.set(overrideTitle);
    }

    if (overrideText) {
      this.text.set(overrideText);
    }

    if (hasBackdrop || this.settings().hasBackdrop) {
      dialog.showModal();
    } else {
      dialog.show();
    }

    if (this.settings().hasBackdrop) {
      this.enableCloseOnBackdrop(this.settings().enableCloseOnBackdrop);
    }

    if (context) {
      this.contentDir.uicontentcontext.set(context);
    }

    if (this.settings().hideStrategy !== UI_POPUP_HIDE_STRATEGY.ClickTrigger) {
      if (!this.modalListener$) {
        this.modalListener$ = fromEvent(document, 'click');
        this.modalListener$
          .pipe(takeUntil(this.modalClosed$))
          .subscribe((event: MouseEvent) => {
            if (
              (this.settings().hideStrategy === 'click_outside' &&
                !this.dialogContainsTarget(event)) ||
              this.settings().hideStrategy === 'click_anywhere' ||
              this.settings().hideStrategy === 'click_outside_or_option' ||
              this.settings().hideStrategy === 'click_outside_or_trigger'
            ) {
              if (dialog.hasAttribute('open')) {
                this.close();
                return;
              }
            }
          });
      }
      event?.stopPropagation();
    }
  }

  public close() {
    let dialog = this.modal()?.nativeElement as HTMLDialogElement;
    dialog.close();
    this.onModalClose?.emit();
    this.modalClosed$.next(true);
    this.modalListener$ = undefined;
    this.enableCloseOnBackdrop(false);
  }

  //#endregion
}
